import { Divider, Flex, theme } from 'antd';
import type { UseQueryResult } from '@tanstack/react-query';
import { CardContent, CardShellEcho } from '@/components/cards/common';
import { palette } from '@acast-tech/echo-theme';
import type { HourlyPlaysHour } from '@/types/content';
import EstimateNotice from './EstimateNotice';
import { hoursOnLocalToday } from './localTodayHours';
import LiveDownloadsChart from './LiveDownloadsChart';
import LiveDownloadsEmptyContent from './LiveDownloadsEmptyContent';
import LiveStatus from './LiveStatus';
import TodaysDownloadsTotal from './TodaysDownloadsTotal';

interface LiveDownloadsCardProps {
  queryResult: UseQueryResult<HourlyPlaysHour[], Error>;
}

const LiveDownloadsBody = ({ data }: { data: HourlyPlaysHour[] }) => {
  const { token } = theme.useToken();
  const lastSyncedTimeUTC = data.at(-1)?.hour_utc;
  const todaysHours = hoursOnLocalToday(data);
  const totalDownloads = todaysHours.reduce((sum, row) => sum + row.listens + row.views, 0);

  return totalDownloads > 0 ? (
    <>
      <Flex align="center" gap={token.marginSM} wrap>
        <LiveStatus />
        <Divider orientation="vertical" style={{ borderColor: token.colorPrimaryBorder }} />
        <TodaysDownloadsTotal totalDownloads={totalDownloads} />
        <LiveDownloadsChart data={todaysHours} />
      </Flex>
      <Divider
        orientation="horizontal"
        style={{ borderColor: token.colorPrimaryBorder, marginBlock: token.marginSM }}
      />
      <EstimateNotice lastSyncedTimeUTC={lastSyncedTimeUTC} />
    </>
  ) : (
    <LiveDownloadsEmptyContent lastSyncedTimeUTC={lastSyncedTimeUTC} />
  );
};

const LiveDownloadsCard = ({ queryResult }: LiveDownloadsCardProps) => {
  const { token } = theme.useToken();

  return (
    <CardShellEcho
      testId="live-downloads-card"
      style={{
        background: `linear-gradient(135deg, ${token.colorPrimaryBg} 0%, ${palette.acastPurpleGrey} 50%, ${palette.acastPurpleGreyAccent} 100%)`,
        borderColor: token.colorPrimaryBorder,
      }}
      bodyStyle={{ paddingBlock: token.paddingSM }}
    >
      <CardContent
        variant="echo"
        isPending={queryResult.isPending}
        isError={queryResult.isError}
        error={queryResult.error}
        data={queryResult.data}
        renderData={(data) => <LiveDownloadsBody data={Array.isArray(data) ? data : []} />}
      />
    </CardShellEcho>
  );
};

export default LiveDownloadsCard;
